<script>
// composable
import { ref, provide } from 'vue';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAI } from 'dashboard/composables/useAI';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

// components
import ReplyBox from './ReplyBox.vue';
import NextMessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import OpenConversationsBanner from './components/OpenConversationsBanner.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import {
  calculateScrollTop,
  calculateScrollTopOnFooterGrowth,
} from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { MESSAGE_TYPE } from 'shared/constants/messages';

import NextButton from 'dashboard/components-next/button/Button.vue';
import CommentTreeModal from 'next/message/CommentTreeModal.vue';

export default {
  components: {
    NextMessageList,
    ReplyBox,
    Banner,
    OpenConversationsBanner,
    ConversationLabelSuggestion,
    NextButton,
    CommentTreeModal,
  },
  mixins: [inboxMixin],
  props: {
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['contactPanelToggle', 'showPreviousConversations'],
  setup() {
    const isPopOutReplyBox = ref(false);
    const conversationPanelRef = ref(null);
    const { isEnterprise } = useConfig();

    // Message context menus lock this panel's scroll while they are open.
    provide('contextMenuElementTarget', conversationPanelRef);

    const closePopOutReplyBox = () => {
      isPopOutReplyBox.value = false;
    };

    const togglePopOutReplyBox = () => {
      isPopOutReplyBox.value = !isPopOutReplyBox.value;
    };

    useKeyboardEvents({
      Escape: {
        action: closePopOutReplyBox,
      },
    });

    const {
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
    } = useAI();

    return {
      isEnterprise,
      isPopOutReplyBox,
      togglePopOutReplyBox,
      isAIIntegrationEnabled,
      isLabelSuggestionFeatureEnabled,
      fetchIntegrationsIfRequired,
      fetchLabelSuggestions,
      conversationPanelRef,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      footerHeight: null,
      isFooterResizeScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      messageId: null,
      programmaticScrollTimeout: null,
      targetScrollTop: null,
      showCommentTree: false,
      highlightedMessageId: null,
      readingInterval: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.isEnterprise &&
        this.isAIIntegrationEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    readingUsersList() {
      return this.$store.getters['conversationReadingStatus/getUserList'](
        this.currentChat.id
      );
    },
    isAnyoneReading() {
      return this.readingUsersList.length !== 0;
    },
    readingUserNames() {
      if (!this.isAnyoneReading) return '';
      const [i18nKey, params] = getTypingUsersText(this.readingUsersList);
      const baseKey = i18nKey.replace('TYPING.', 'READING.');
      return this.$t(baseKey, params);
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }

      return messages.filter(
        message => !(message.private && message.content_attributes?.deleted)
      );
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },

    isFacebookComment() {
      return this.conversationType === 'facebook_comment';
    },
    isATweet() {
      return this.conversationType === 'tweet';
    },
    isRightOrLeftIcon() {
      if (this.isContactPanelOpen) {
        return 'arrow-chevron-right';
      }
      return 'arrow-chevron-left';
    },
    getLastSeenAt() {
      const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
      return contactLastSeenAt;
    },

    // An Instagram conversation in a Facebook inbox whose Instagram account
    // also has its own Instagram inbox. Messenger conversations do not count.
    hasDuplicateInstagramInbox() {
      if (this.inbox.channel_type !== INBOX_TYPES.FB || !this.isInstagramDM) {
        return false;
      }
      return !!this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
        this.inbox.instagram_id
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING);

      return { incoming, outgoing };
    },
    showCurrentChatLink() {
      return (
        !this.currentChat.can_reply &&
        this.currentChat?.link &&
        (this.isAFacebookInbox || this.isAInstagramChannel)
      );
    },
    conversationLink() {
      if (this.currentChat?.link) {
        return `https://business.facebook.com${this.currentChat.link}`;
      }
      return '';
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
    offlineContactReplyAllowed() {
      const flags = this.inbox?.selected_feature_flags || [];
      return flags.includes('allow_offline_reply');
    },
    showWebWidgetOfflineBanner() {
      if (
        !this.currentChat.can_reply ||
        !this.isAWebWidgetInbox ||
        this.currentContact?.availability_status === 'online'
      ) {
        return false;
      }

      const hasValidEmail =
        this.currentContact?.email && this.currentContact.email.trim() !== '';
      const canUseContinuityEmail =
        this.inbox?.continuity_via_email && hasValidEmail;

      return !canUseContinuityEmail && !this.offlineContactReplyAllowed;
    },
    showWebWidgetOfflineWarningBanner() {
      if (
        !this.currentChat.can_reply ||
        !this.isAWebWidgetInbox ||
        this.currentContact?.availability_status === 'online'
      ) {
        return false;
      }

      const hasValidEmail =
        this.currentContact?.email && this.currentContact.email.trim() !== '';
      const canUseContinuityEmail =
        this.inbox?.continuity_via_email && hasValidEmail;
      return !canUseContinuityEmail && this.offlineContactReplyAllowed;
    },
    showPendingWithBotBanner() {
      if (this.showWebWidgetOfflineBanner) {
        return false;
      }
      return (
        this.currentChat?.status === 'pending' && this.inbox?.agent_bot?.id
      );
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReadingInterval();
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    emitter.on(BUS_EVENTS.SHOW_COMMENT_TREE, this.openCommentTree);
    // when a new message comes in, we refetch the label suggestions
    emitter.on(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    this.startReadingInterval();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
    this.stopReadingInterval();
    if (this.readingDelayTimeout) {
      clearTimeout(this.readingDelayTimeout);
      this.readingDelayTimeout = null;
    }
    // Clean up programmatic scroll timeout
    if (this.programmaticScrollTimeout) {
      clearTimeout(this.programmaticScrollTimeout);
      this.programmaticScrollTimeout = null;
    }
    this.targetScrollTop = null;
  },

  methods: {
    sendReadingStatus() {
      const conversationId = this.currentChat?.id;
      if (!conversationId) return;
      this.$store.dispatch('conversationReadingStatus/markReading', {
        conversationId,
      });
    },
    startReadingInterval() {
      this.readingDelayTimeout = setTimeout(() => {
        this.readingDelayTimeout = null;
        this.sendReadingStatus();
        this.readingInterval = setInterval(this.sendReadingStatus, 30000);
      }, 5000);
    },
    stopReadingInterval() {
      if (this.readingDelayTimeout) {
        clearTimeout(this.readingDelayTimeout);
        this.readingDelayTimeout = null;
      }
      if (this.readingInterval) {
        clearInterval(this.readingInterval);
        this.readingInterval = null;
      }
    },
    resetReadingInterval() {
      this.stopReadingInterval();
      this.startReadingInterval();
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      if (!this.isEnterprise) {
        return;
      }

      // method available in mixin, need to ensure that integrations are present
      await this.fetchIntegrationsIfRequired();

      if (!this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.fetchLabelSuggestions({
        conversationId: this.currentChat.id,
      });

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        this.messageId = this.messageId || messageId || null;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!this.messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.SHOW_COMMENT_TREE, this.openCommentTree);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      let targetMessageId = messageId;

      if (targetMessageId === '') {
        const filteredUnreadMessages = this.unReadMessages.filter(
          message =>
            message.message_type === MESSAGE_TYPE.INCOMING ||
            message.message_type === MESSAGE_TYPE.OUTGOING
        );

        if (filteredUnreadMessages.length > 0) {
          targetMessageId = filteredUnreadMessages[0].id;
        } else {
          const filteredMessages = this.getMessages.filter(
            message =>
              message.message_type === MESSAGE_TYPE.INCOMING ||
              message.message_type === MESSAGE_TYPE.OUTGOING
          );

          if (filteredMessages.length > 0) {
            targetMessageId = filteredMessages[filteredMessages.length - 1].id;
          }
        }
      }

      this.messageId = targetMessageId;

      this.scrollToMessageWhenReady();
      this.makeMessagesRead();
    },
    scrollToMessageWhenReady() {
      const isReady =
        (this.currentChat?.dataFetched === true ||
          (this.currentChat?.dataFetched === undefined &&
            this.getMessages?.length > 0)) &&
        !this.isLoadingPrevious;

      if (isReady) {
        this.waitForMessageElementAndScroll();
      } else {
        const unwatch = this.$watch(
          () => ({
            dataFetched: this.currentChat?.dataFetched,
            isLoadingPrevious: this.isLoadingPrevious,
            messagesCount: this.getMessages?.length,
          }),
          newVal => {
            const watcherIsReady =
              (newVal.dataFetched === true ||
                (newVal.dataFetched === undefined &&
                  newVal.messagesCount > 0)) &&
              !newVal.isLoadingPrevious;

            if (watcherIsReady) {
              this.waitForMessageElementAndScroll();
              unwatch();
            }
          },
          { immediate: false }
        );
      }
    },
    waitForMessageElementAndScroll() {
      const messageElement = document.getElementById(
        'message' + this.messageId
      );

      if (messageElement) {
        this.isProgrammaticScroll = true;
        this.targetScrollTop = messageElement.offsetTop;
        messageElement.scrollIntoView({ behavior: 'smooth' });
      }
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    onFooterResize([entry]) {
      // `offsetHeight` matches the laid out box, `contentRect` does not.
      const height = entry?.target?.offsetHeight ?? 0;
      const previousFooterHeight = this.footerHeight;
      this.footerHeight = height;

      // Nothing to compare against on the first measurement. In pop out mode the footer
      // is `fixed inset-0`, so its height is meaningless and it no longer contributes to
      // the panel's scroll height. Coming back inline yields a negative delta, which is
      // ignored, so the baseline heals itself.
      if (previousFooterHeight === null || this.isPopOutReplyBox) return;

      this.pinFooterAfterGrowth(
        height - previousFooterHeight,
        previousFooterHeight
      );
    },
    pinFooterAfterGrowth(growth, previousFooterHeight) {
      const panel = this.conversationPanel;
      if (!panel) return;

      const scrollTop = calculateScrollTopOnFooterGrowth({
        scrollTop: panel.scrollTop,
        scrollHeight: panel.scrollHeight,
        clientHeight: panel.clientHeight,
        growth,
        previousFooterHeight,
      });
      if (scrollTop === null) return;

      const scrollTopBeforeAdjustment = panel.scrollTop;
      panel.scrollTop = scrollTop;
      // A `scroll` event only fires when the value actually changed. Arming the guard
      // otherwise would swallow the user's next real scroll.
      if (panel.scrollTop !== scrollTopBeforeAdjustment) {
        this.isFooterResizeScroll = true;
      }
    },
    onToggleContactPanel() {
      this.$emit('contactPanelToggle');
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      // Re-pinning the composer after it grew is not a user scroll, and it always moves
      // us away from the top, so neither `hasUserScrolled` nor `fetchPreviousMessages`
      // should react to it.
      if (this.isFooterResizeScroll) {
        this.isFooterResizeScroll = false;
        return;
      }
      if (this.isProgrammaticScroll) {
        this.hasUserScrolled = false;

        if (this.programmaticScrollTimeout) {
          clearTimeout(this.programmaticScrollTimeout);
        }

        this.programmaticScrollTimeout = setTimeout(() => {
          this.isProgrammaticScroll = false;
          this.programmaticScrollTimeout = null;

          if (this.targetScrollTop !== null) {
            const currentScrollTop = e.target.scrollTop;
            const scrollHeight = e.target.scrollHeight;
            const clientHeight = e.target.clientHeight;
            const distanceFromTarget = Math.abs(
              currentScrollTop - this.targetScrollTop
            );
            const atBottom =
              currentScrollTop + clientHeight >= scrollHeight - 50;

            if (distanceFromTarget < 200 || atBottom) {
              this.fetchPreviousMessages();
            }

            this.targetScrollTop = null;
          }
        }, 1000);

        return;
      }
      this.hasUserScrolled = true;

      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    openCommentTree({ messageId }) {
      this.highlightedMessageId = messageId;
      this.showCommentTree = true;
    },
    getInReplyToMessage(parentMessage) {
      if (!parentMessage) return {};
      const inReplyToMessageId = parentMessage.content_attributes?.in_reply_to;
      if (!inReplyToMessageId) return {};

      return this.currentChat?.messages.find(message => {
        if (message.id === inReplyToMessageId) {
          return true;
        }
        return false;
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <Banner
      v-if="!currentChat.can_reply"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="replyWindowBannerMessage"
      :href-link="replyWindowLink"
      :href-link-text="replyWindowLinkText"
    />
    <Banner
      v-else-if="hasDuplicateInstagramInbox"
      data-testid="old-instagram-inbox-banner"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
    />
    <Banner
      v-if="showCurrentChatLink"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.FACEBOOK_CANNOT_REPLY')"
      :href-link="conversationLink"
      :href-link-text="$t('CONVERSATION.CLICK_HERE')"
    />
    <Banner
      v-if="showWebWidgetOfflineBanner"
      color-scheme="alert"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.WEB_WIDGET_OFFLINE_MESSAGE')"
    />
    <Banner
      v-if="showWebWidgetOfflineWarningBanner"
      color-scheme="warning"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      :banner-message="$t('CONVERSATION.WEB_WIDGET_OFFLINE_WARNING_MESSAGE')"
    />
    <Banner
      v-if="showPendingWithBotBanner"
      :banner-message="$t('CONVERSATION.PENDING_WITH_BOT_BANNER')"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      color-scheme="alert"
    />
    <Banner
      v-if="inbox?.replies_paused"
      data-testid="inbox-delete-requested-banner"
      :banner-message="$t('CONVERSATION.INBOX_DELETE_REQUESTED_BANNER')"
      class="mx-2 mt-2 overflow-hidden rounded-lg"
      color-scheme="alert"
    />
    <OpenConversationsBanner
      :contact-id="currentChat.meta?.sender?.id"
      :current-conversation-id="currentChat.id"
      @show-previous-conversations="$emit('showPreviousConversations')"
    />
    <div class="flex justify-end">
      <NextButton
        faded
        xs
        slate
        class="!rounded-r-none rtl:rotate-180 !rounded-2xl !fixed z-10"
        :icon="
          isContactPanelOpen ? 'i-ph-caret-right-fill' : 'i-ph-caret-left-fill'
        "
        :class="isInboxView ? 'top-52 md:top-40' : 'top-32'"
        @click="onToggleContactPanel"
      />
    </div>
    <NextMessageList
      ref="conversationPanelRef"
      class="conversation-panel"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :is-a-phone-channel="isPhoneInbox"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li class="min-h-[4rem]">
            <span v-if="shouldShowSpinner" class="spinner message" />
          </li>
        </transition>
        <div
          v-if="isAnyoneReading"
          class="sticky z-10 flex items-center w-full top-2"
        >
          <div
            class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-slate-700 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
          >
            {{ readingUserNames }}
          </div>
        </div>
      </template>
      <template #unreadBadge>
        <div
          v-show="unreadMessageCount !== 0"
          class="flex justify-center text-sm my-4 mx-0 py-2 pr-0.5 pl-2.5 bg-woot-500 dark:bg-woot-500 text-white dark:text-white rounded-full border border-woot-500 dark:border-woot-500 border-solid shadow-lg"
        >
          <div class="inline-flex items-center text-center">
            {{ unreadMessageLabel }}
          </div>
        </div>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
        <div
          v-resize="onFooterResize"
          class="conversation-footer"
          :class="{
            'modal-mask': isPopOutReplyBox,
            'bg-n-background': !isPopOutReplyBox,
          }"
          @mousedown.self="isPopOutReplyBox && togglePopOutReplyBox()"
        >
          <div
            v-if="isAnyoneTyping"
            class="absolute flex items-center w-full h-0 -top-7"
          >
            <div
              class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-slate-700 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
            >
              {{ typingUserNames }}
              <img
                class="w-6 ltr:ml-2 rtl:mr-2"
                src="assets/images/typing.gif"
                :alt="$t('TYPING.IMAGE_ALT')"
              />
            </div>
          </div>
          <ReplyBox
            :popout-reply-box="isPopOutReplyBox"
            @toggle-popout="togglePopOutReplyBox"
          />
        </div>
      </template>
    </NextMessageList>
    <CommentTreeModal
      v-if="isFacebookComment"
      :show="showCommentTree"
      :messages="getMessages"
      :highlighted-message-id="highlightedMessageId"
      @close="showCommentTree = false"
    />
  </div>
</template>

<style scoped lang="scss">
.modal-mask {
  @apply fixed inset-0 z-50 flex items-start justify-center pt-[clamp(1rem,5vh,6rem)] bg-modal-backdrop-light dark:bg-modal-backdrop-dark;

  &::v-deep {
    .banner--self-assign {
      @apply w-[70%] mx-auto;
    }

    .reply-box {
      @apply border border-n-weak w-[70%] mx-auto bg-n-solid-1;

      &.is-private {
        @apply dark:border-n-amber-3/30 border-n-amber-12/5;
      }
    }

    .reply-box .reply-box__top {
      @apply relative flex-1 min-h-0;
    }

    .ProseMirror-woot-style {
      @apply max-h-none h-full;
    }

    .emoji-dialog {
      @apply absolute left-auto bottom-1;
    }
  }
}
</style>
